/**
 * Labels, formatting and the completeness questions the saya screens read.
 *
 * Pure by construction, and unit-tested next door. Everything here informs —
 * warnings, chips, field lists — and nothing gates: the approval ceremony is
 * gone by owner decision (docs/saya-atolyesi-design.md §1), and the Worker
 * still revalidates every write on its own.
 */

import type { BadgeProps } from '@app/components/ui/badge'
import type { QualityFlag } from '@app/lib/trace'
import { PROSES_NAMES } from '../../../worker/api/production.contract'
import {
  MAX_SCANS_PER_REVISION,
  MM2_PER_DESI,
  mm2ToDesi,
  type BomLineOption,
  type CalibrationSource,
  type Handedness,
  type Orientation,
  type PurchaseUnit,
  type RevisionSource,
  type SayaPartDto,
} from '../../../worker/api/saya.contract'

export type StatusTone = BadgeProps['tone']

/* ── Numbers ────────────────────────────────────────────────────────────────── */

const desiFormat = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const countFormat = new Intl.NumberFormat('tr-TR')

/*
 * The desi conversion is the contract's (`worker/api/saya.contract.ts`), not a
 * second copy of it: the Worker measures in mm² and this file labels the same
 * number, so the factor has to be one value both of them read. Re-exported here
 * because this is where the screens already look for saya formatting.
 */
export { MM2_PER_DESI, mm2ToDesi }

/** Always two decimals — a desi reads as money-shaped to the people using it. */
export function formatDesi(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : desiFormat.format(value)
}

export function formatCount(value: number): string {
  return countFormat.format(value)
}

/**
 * The numeral shared by every representation of a piece.
 *
 * A set with several sheets needs the sheet prefix because piece names and
 * row positions both restart on each sheet. Keeping the result keyed by the
 * piece's stable key lets the rail, canvas and layout strips consume the exact
 * same string instead of reproducing the positional rule.
 */
export function pieceNumbers(
  sheets: readonly { pieces: readonly { key: string }[] }[],
): Map<string, string> {
  const numbers = new Map<string, string>()
  const prefixed = sheets.length > 1
  for (const [sheetIndex, sheet] of sheets.entries()) {
    for (const [pieceIndex, piece] of sheet.pieces.entries()) {
      numbers.set(
        piece.key,
        prefixed ? `${sheetIndex + 1}.${pieceIndex + 1}` : String(pieceIndex + 1),
      )
    }
  }
  return numbers
}

/** A ratio as the percentage a person says out loud: `%68`. */
export function formatPercent(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : `%${countFormat.format(Math.round(value * 100))}`
}

/**
 * The factory's own calendar day, which is the only one anybody here means.
 *
 * The saya tables are the module's OWN (`app` schema), and their timestamps are
 * `timestamp with time zone` serialized with a real `Z` — unlike the Korgun
 * mirror's naked wall-clock strings, which the modeller module slices. Slicing
 * these would print the UTC day: everything a late shift records between
 * midnight and 03:00 Istanbul would be stamped the day before, on the screen
 * that says who approved a revision and when.
 *
 * So a zoned string is converted, and a naked one is still split — the same
 * value read from either kind of column renders as the day it happened.
 */
const istanbulDate = new Intl.DateTimeFormat('tr-TR', {
  timeZone: 'Europe/Istanbul',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/** True when the string carries a UTC `Z` or a `+03:00`-style offset. */
function hasTimeZone(iso: string): boolean {
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso)
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  if (hasTimeZone(iso)) {
    const at = new Date(iso)
    if (Number.isNaN(at.getTime())) return '—'
    return istanbulDate.format(at)
  }
  const [year, month, day] = iso.slice(0, 10).split('-')
  if (!year || !month || !day) return '—'
  return `${day}.${month}.${year}`
}

/* ── Vocabulary ─────────────────────────────────────────────────────────────── */

export interface StatusView {
  label: string
  tone: StatusTone
}

export const HANDEDNESS_LABELS: Record<Handedness, string> = {
  aynali_cift: 'Aynalı çift',
  tek: 'Tek',
  ayni_yonlu: 'Aynı yönlü',
}

export const ORIENTATION_LABELS: Record<Orientation, string> = {
  tek_yon: 'Tek yön',
  cift_yon: 'Çift yön (0°/180°)',
  serbest: 'Serbest',
}

export const PURCHASE_UNIT_LABELS: Record<PurchaseUnit, string> = {
  desi: 'desi',
  mt: 'metre',
  tabaka: 'tabaka',
}

export const SOURCE_LABELS: Record<RevisionSource, string> = {
  scan: 'Tarama',
  dxf: 'DXF',
}

/* ── Quality ────────────────────────────────────────────────────────────────── */

/**
 * Codes the trace no longer raises but archived scan rows still carry.
 *
 * `border_contact` and `dropped_void` were removed from detection (the first
 * was pure noise on real sheets, the second described hole tracing, which is
 * gone). Rows written before that keep the codes, and they must keep rendering
 * their real sentences rather than demoting to "Bilinmeyen kalite uyarısı".
 */
export type LegacyQualityCode = 'border_contact' | 'dropped_void'

/**
 * What the trace lib's flag codes mean to an operator.
 *
 * Every flag is an informational note — none of them refuses a save or an
 * approval — so each sentence states what the trace saw and, where there is
 * one, the remedy the operator MAY take.
 *
 * Keyed by the trace library's own union (plus the legacy codes above) rather
 * than by `string`, so a new code added to `lib/trace` fails to compile until
 * it has a Turkish sentence here.
 */
export const QUALITY_FLAG_MESSAGES: Record<QualityFlag['code'] | LegacyQualityCode, string> = {
  touching_suspect: 'İki parça birbirine değiyor görünüyor — kalıpları ayırıp yeniden tarayın.',
  too_small: 'Parça çok küçük — büyük olasılıkla camdaki toz, kalıp değil.',
  invalid_ring: 'Konturu okunamadı — kalıbın kenarı belirsiz, yeniden tarayın.',
  /*
   * Legacy: stored rows only. The wording is kept exactly as those rows were
   * shown when they were written.
   */
  border_contact: 'Parça tarama kenarına değiyor — bir bölümü eksik olabilir, kalıbı ortalayıp yeniden tarayın.',
  /*
   * Legacy: stored rows only. A statement about what that trace did, and
   * nothing else — it promises no check, because none of the ones the original
   * wording pointed at exist any more. The marks this flag reported are, on a
   * 300 dpi scan of a real kalıp, the pencil the model room writes the code and
   * the size with.
   */
  dropped_void:
    'Bu tarama yapıldığında küçük iç izler delik olarak alınmıyordu (kalem izi, toz).',
}

/**
 * The Turkish sentence for one flag code.
 *
 * A code this build does not know — a flag written by a newer client and read
 * back off the scan row — gets a named unknown, NOT the flag's `detail`: detail
 * is English diagnostic text for logs (`lib/trace/quality.ts` and the contract
 * both say so), and rendering it would put English in front of an operator who
 * cannot read it and could not act on it either.
 */
export function qualityFlagMessage(code: string): string {
  const message = (QUALITY_FLAG_MESSAGES as Record<string, string | undefined>)[code]
  // "not", not "uyarı": flags gate nothing any more, and an unknown code is an
  // unknown remark, not an unknown threat.
  return message ?? `Bilinmeyen tarama notu (${code}) — taramayı kontrol edin.`
}

export interface Verdict extends StatusView {
  ok: boolean
  /** The sentence under the badge when there are no flags to list. */
  message: string
}

export interface ScanState {
  /** True while the browser is still tracing this sheet. */
  tracing: boolean
  /** Pieces the trace produced on this sheet. */
  pieceCount: number
  /**
   * Notes the trace's checks raised about this sheet. Informational only —
   * they refuse nothing; the count exists so the bar can say they are there.
   */
  flagCount: number
}

/**
 * The banner over the canvas: one sentence, one colour, no hedging.
 *
 * Zero pieces is its OWN verdict and not a clean one. A dark photograph, an
 * inverted scan or an empty bed produces no shapes and therefore no flags, and
 * reading that as "Uygun" told the operator their scan had passed at the exact
 * moment it had failed hardest.
 */
export function scanVerdict(state: ScanState): Verdict {
  if (state.tracing) {
    return {
      label: 'Çiziliyor',
      tone: 'info',
      ok: false,
      message: 'Kalıpların izi çıkarılıyor…',
    }
  }
  if (state.pieceCount === 0) {
    return {
      label: 'Parça bulunamadı',
      tone: 'warn',
      ok: false,
      message:
        'Kalıplar koyu zemin üzerinde ve birbirinden ayrık mı? Taramayı kontrol edip yeniden yükleyin.',
    }
  }
  if (state.flagCount > 0) {
    // Notes, not gates: the set saves and approves regardless. The verdict
    // still names them so the operator knows there is something to read — but
    // it says "not", never "uyarı": warning vocabulary belonged to the removed
    // blocking checks, and a word that once meant "fix this first" must not
    // keep ringing after the gate is gone.
    return {
      label: 'Notlar var',
      tone: 'warn',
      ok: true,
      message: 'Tarama notları listede — kayda engel değildir.',
    }
  }
  return {
    label: 'Uygun',
    tone: 'ok',
    ok: true,
    message: 'Kalite kontrolleri temiz — parçalar ayrık, kenarlar okunur.',
  }
}

/* ── Calibration, as the screen states it ───────────────────────────────────── */

export const MM_PER_INCH = 25.4

/** The scanner's own unit. 11,81 px/mm is 300 dpi, which is what it stamps. */
export function dpiFromPxPerMm(pxPerMm: number): number {
  return Math.round(pxPerMm * MM_PER_INCH)
}

export interface CalibrationView {
  /** The quiet chip, e.g. `300 DPI · dosyadan`; null when nothing is measured. */
  chip: string | null
  /** Set only for the one state approval refuses — short, and it names the fix. */
  warning: string | null
}

/**
 * What a sheet knows about its own millimetres, in one chip.
 *
 * The factory's flatbed stamps 300 DPI into every file it writes and the upload
 * reads it (`worker/lib/saya/scan-stream.ts`), so the ordinary case is a scan
 * that arrived calibrated with nobody typing anything — and the ordinary case
 * deserves a muted chip, not a warning. There used to be a paragraph here
 * telling the operator their measurements were an A4 guess; the guess is gone,
 * and so is the paragraph.
 *
 * `manual` says a human measured it, which is worth showing because it OVERRIDES
 * what the file claims. A sheet with numbers but no recorded source is a row
 * written before the source column existed: it is calibrated, and the chip says
 * only that.
 *
 * The `null` state is the only one that gets a colour, because it is the only
 * one that blocks approval — and its one exit is a sentence long: rescan on the
 * factory scanner, whose files always carry their own DPI. There is no manual
 * entry to point at any more; the screen's calibration UI is gone by owner
 * decision, and the API's PUT endpoint stays as an unadvertised recovery path.
 */
export function calibrationView(scan: {
  pxPerMmX: number | null
  pxPerMmY: number | null
  calibrationSource: CalibrationSource | null
}): CalibrationView {
  if (scan.pxPerMmX == null || scan.pxPerMmY == null) {
    return {
      chip: null,
      warning:
        'Bu dosyada güvenilir DPI bilgisi yok — fabrika tarayıcısından yeniden tarayın.',
    }
  }

  const dpiX = dpiFromPxPerMm(scan.pxPerMmX)
  const dpiY = dpiFromPxPerMm(scan.pxPerMmY)
  const dpi = dpiX === dpiY ? formatCount(dpiX) : `${formatCount(dpiX)}×${formatCount(dpiY)}`
  const origin =
    scan.calibrationSource === 'file_dpi'
      ? ' · dosyadan'
      : scan.calibrationSource === 'manual'
        ? ' · elle'
        : ''
  return { chip: `${dpi} DPI${origin}`, warning: null }
}

/* ── Uploading several sheets at once ───────────────────────────────────────── */

export { MAX_SCANS_PER_REVISION }

export interface UploadPlan {
  /** How many of the chosen files may be uploaded, in the order they were chosen. */
  accepted: number
  /** The one sentence about the cap, or null when every file fits. */
  note: string | null
}

/**
 * How much of a selection fits in the set — decided BEFORE the first byte moves.
 *
 * A saya set does not fit on one bed, so the ordinary thing an operator does is
 * lay out four or five sheets and pick them all at once. The Worker refuses the
 * twenty-first with a 409, but discovering the ceiling from a failed request in
 * the middle of a queue is how a person ends up not knowing which sheets landed.
 * So the browser counts the free slots first and says, in one sentence, exactly
 * what it is about to do.
 */
export function planUpload(selected: number, existingSheets: number): UploadPlan {
  const free = Math.max(0, MAX_SCANS_PER_REVISION - existingSheets)
  if (selected <= free) return { accepted: selected, note: null }
  if (free === 0) {
    return {
      accepted: 0,
      note: `Sette zaten ${formatCount(MAX_SCANS_PER_REVISION)} tarama var — daha fazlası eklenemez. Gereksiz taramaları silip yeniden deneyin.`,
    }
  }
  /*
   * "alındı", not "yüklendi": this sentence is written BEFORE the first byte
   * moves, so a past tense about the upload would be claiming something that has
   * not happened yet — and if one of the taken files then fails, its own line
   * would contradict this one. What is true at this moment, and stays true, is
   * which files the set had room for.
   */
  return {
    accepted: free,
    note: `Bir sette en fazla ${formatCount(MAX_SCANS_PER_REVISION)} tarama olabilir: seçtiğiniz ${formatCount(selected)} dosyanın ilk ${formatCount(free)} tanesi alındı, kalan ${formatCount(selected - free)} dosya alınmadı.`,
  }
}

/** "Tarama 3/5 yükleniyor — arka.jpg" for the strip, while the queue runs. */
export function uploadProgressText(done: number, total: number, name: string): string {
  return `Tarama ${formatCount(Math.min(done + 1, total))}/${formatCount(total)} yükleniyor — ${name}`
}

/**
 * One failed file, named, so the operator knows which sheet to lay out again.
 *
 * `othersContinue` is what turns a dead end into a report: in a queue the rest
 * of the selection is still being uploaded, and a line that did not say so would
 * read as "the upload failed" when four sheets of five are already in the set.
 * It means "there are files AFTER this one", not "the selection had several" —
 * the last file of five failing has nothing left to carry on.
 */
export function uploadFailureText(name: string, reason: string, othersContinue: boolean): string {
  // API sentences arrive punctuated; this one supplies its own full stop.
  const said = reason.trim().replace(/\.+$/, '')
  return othersContinue
    ? `${name} yüklenemedi: ${said}. Diğer dosyalar yüklenmeye devam etti — bu taramayı tek başına tekrar ekleyin.`
    : `${name} yüklenemedi: ${said}.`
}

/* ── BOM lines ──────────────────────────────────────────────────────────────── */

/** Radix Select refuses an empty value, so "not in the BOM" needs a token. */
export const NO_BOM_VALUE = 'yok'

export type BomRef = NonNullable<SayaPartDto['bomRef']>

/**
 * The processes a saya piece can actually be cut on: leather/upper cutting and
 * lining cutting. Every other process on the model's recipe — soles, buckles,
 * lasts — is a line no traced saya piece ever comes from, so those are offered
 * second and under their own heading rather than mixed in where a wrong pick is
 * one keystroke away.
 */
export const CUTTING_PROCESSES: readonly string[] = ['01', '08']

export function bomLineKey(line: BomLineOption): string {
  return `${line.proses}#${line.parcainx}#${line.skod}`
}

export function bomRefKey(ref: BomRef | null): string {
  return ref ? `${ref.proses}#${ref.parcainx}#${ref.skod}` : NO_BOM_VALUE
}

export function bomLineLabel(line: BomLineOption): string {
  const proses = PROSES_NAMES[line.proses] ?? line.proses
  return `${line.skod} · ${line.tanim} · ${proses} ${formatCount(line.parcainx)}`
}

/** The option list split in two: what a saya piece is cut from, and the rest. */
export function bomLineGroups(lines: readonly BomLineOption[]): {
  cutting: BomLineOption[]
  other: BomLineOption[]
} {
  const cutting: BomLineOption[] = []
  const other: BomLineOption[] = []
  for (const line of lines) {
    if (CUTTING_PROCESSES.includes(line.proses)) cutting.push(line)
    else other.push(line)
  }
  return { cutting, other }
}

/** Turns a select value back into a ref, ignoring anything unrecognised. */
export function bomRefFromKey(key: string, lines: readonly BomLineOption[]): BomRef | null {
  const line = lines.find((candidate) => bomLineKey(candidate) === key)
  return line ? { proses: line.proses, parcainx: line.parcainx, skod: line.skod } : null
}

/* ── Part completeness ──────────────────────────────────────────────────────── */

/** What the rail edits — the subset of a part a human fills in. */
export interface PartFormValues {
  name: string
  materialId: number | null
  qtyPerPair: number
  handedness: Handedness
  orientation: Orientation
  /**
   * Grain/nap unit vector in the PIECE-LOCAL frame — rotating the pose does
   * not rewrite it; display rotates it via `rotateUnitVec`. Null while the
   * operator has not answered the direction question.
   */
  directionVec: [number, number] | null
  bomRef: BomRef | null
  /**
   * Whether the BOM question has been ANSWERED, which `bomRef: null` cannot say
   * on its own — it is both "not chosen yet" and the deliberate "BOM'da yok".
   * Screen-side only: the wire has one field and the server stores one field, so
   * a part reloaded with no BOM line reads as unanswered again and is nudged
   * once more. That nag is the honest version; a silent "BOM'da yok" on every
   * untouched part quietly forfeits the Korgun comparison the module exists for.
   */
  bomAnswered: boolean
}

/**
 * A freshly traced shape starts as a mirrored pair of two, because that is what
 * almost every saya part is. `serbest` is the safe orientation default: a part
 * wrongly marked directional only costs leather, one wrongly marked free costs
 * a mis-cut nap.
 */
export const DEFAULT_PART_VALUES: Omit<PartFormValues, 'name'> = {
  materialId: null,
  qtyPerPair: 2,
  handedness: 'aynali_cift',
  orientation: 'serbest',
  directionVec: null,
  bomRef: null,
  bomAnswered: false,
}

/** The quantity range the Worker accepts on `PUT /api/saya/parts/:id`. */
export const MIN_QTY_PER_PAIR = 1
export const MAX_QTY_PER_PAIR = 99

/** The name length the Worker accepts. */
export const MAX_PART_NAME_LENGTH = 120

/**
 * What the server would REFUSE about this part, worded for the operator, or
 * null when it would take it.
 *
 * Not the same question as `partMissingFields`: a part may be saved
 * half-labelled (that is what saving progress means), but a part the API
 * answers 400 to strands the save mid-set with "Geçersiz istek gövdesi." and no
 * clue which piece it meant. This is asked before the first request goes out.
 */
export function partSaveIssue(values: PartFormValues): string | null {
  const name = values.name.trim()
  if (name === '') return 'adı boş'
  if (name.length > MAX_PART_NAME_LENGTH) return `adı ${formatCount(MAX_PART_NAME_LENGTH)} karakteri aşıyor`
  if (
    !Number.isInteger(values.qtyPerPair) ||
    values.qtyPerPair < MIN_QTY_PER_PAIR ||
    values.qtyPerPair > MAX_QTY_PER_PAIR
  ) {
    return `adet/çift ${formatCount(MIN_QTY_PER_PAIR)} ile ${formatCount(MAX_QTY_PER_PAIR)} arasında olmalı`
  }
  return null
}

/**
 * True when two label sets would produce the same `PUT /api/saya/parts/:id`.
 *
 * `bomAnswered` is deliberately not compared: it never leaves the browser, so
 * answering "BOM'da yok" on a part that already had no BOM line is not a change
 * the server has to hear about. This is what lets a save send only the pieces
 * that moved instead of one request per piece on every click.
 */
/**
 * Tolerance compare, NOT quantize-then-compare: the client rounds a
 * suggested vector to 1e-4, the server renormalizes it (÷hypot), and the
 * drift is ≤6e-5 — but a quantized key still split at bucket boundaries
 * (the 1e-4-rounded inputs land exactly on .5 boundaries ~10% of the time).
 * A tolerance has no boundaries to straddle. 1e-3 hides at most ~0.08° of
 * angle — far below any nap or pattern distinction — and is ~17× the drift.
 */
const DIRECTION_TOLERANCE = 1e-3

function sameDirection(a: [number, number] | null, b: [number, number] | null): boolean {
  if (a === null || b === null) return a === b
  return (
    Math.abs(a[0] - b[0]) < DIRECTION_TOLERANCE && Math.abs(a[1] - b[1]) < DIRECTION_TOLERANCE
  )
}

export function sameLabels(a: PartFormValues, b: PartFormValues): boolean {
  return (
    a.name === b.name &&
    a.materialId === b.materialId &&
    a.qtyPerPair === b.qtyPerPair &&
    a.handedness === b.handedness &&
    a.orientation === b.orientation &&
    sameDirection(a.directionVec, b.directionVec) &&
    bomRefKey(a.bomRef) === bomRefKey(b.bomRef)
  )
}

/**
 * Which required fields a part is still missing, named the way they are
 * labelled on screen. The BOM line is deliberately NOT required — "BOM'da yok"
 * is a real answer for a fortluk that was never entered in Korgun.
 */
export function partMissingFields(values: PartFormValues): string[] {
  const missing: string[] = []
  if (values.name.trim() === '') missing.push('ad')
  if (values.materialId == null) missing.push('malzeme')
  if (!Number.isInteger(values.qtyPerPair) || values.qtyPerPair < 1) missing.push('adet/çift')
  return missing
}

export function partIsComplete(values: PartFormValues): boolean {
  return partMissingFields(values).length === 0
}

/** The editable half of a stored part, lifted out of its DTO for the rail. */
export function partValuesFromDto(part: SayaPartDto): PartFormValues {
  return {
    name: part.name,
    materialId: part.materialId,
    qtyPerPair: part.qtyPerPair,
    handedness: part.handedness,
    orientation: part.orientation,
    directionVec: part.directionVec,
    bomRef: part.bomRef,
    bomAnswered: part.bomRef != null,
  }
}

/**
 * A directional material placed at any angle is the mistake this module exists
 * to stop: nubuk and süet have a nap, and a piece rotated against it comes off
 * the cutter a visibly different colour. It is a warning rather than a
 * refusal — the material flag is a card in our own table, and the operator
 * holding the piece knows better than the card does.
 */
export function directionWarning(
  material: { name: string; directional: boolean } | undefined,
  orientation: Orientation,
): string | null {
  if (!material?.directional || orientation !== 'serbest') return null
  return `${material.name} yönlü bir malzeme — “serbest” yerleşim tonu bozabilir.`
}
